import React, { useState, useEffect } from 'react';
import './Footer.css';
import { Link } from 'react-router-dom';
import apiService from '../services/api';

/**
 * Footer — Fully dynamic component that fetches data from API
 * Automatically loads website info and displays it
 */
export default function Footer() {
  const [websiteInfo, setWebsiteInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  // Default values as fallback
  const defaults = {
    app_name: 'Devnar Foundation for the Blind',
    logo_url: '/logo.png',
    contact_email: 'saibaba_goud@hotmail.com',
    phone_number: '(+91) 040 27767931, 9494940009',
    address_en: '<p>Plot No: 185, Road No 1, West Marredpally, Secunderabad 500026.</p>',
    facebook: null,
    youtube: null,
    linkedin: null,
    instagram: null,
    twitter: null,
    website_url: '#'
  };

  // Fetch website info on component mount using apiService
  useEffect(() => {
    const fetchWebInfo = async () => {
      try {
        const result = await apiService.fetchWebinfo();
        
        if (result.status === 'success' && result.data && result.data.length > 0) {
          setWebsiteInfo(result.data[0]);
        } else {
          setWebsiteInfo(defaults);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching web info:', error);
        setWebsiteInfo(defaults);
        setLoading(false);
      }
    };

    fetchWebInfo();
  }, []);

  // Parse address HTML to clean text
  const parseAddress = (htmlString) => {
    if (!htmlString) return ['Address not available'];
    const temp = document.createElement('div');
    temp.innerHTML = htmlString;
    const text = temp.textContent || temp.innerText || '';
    return text.split(/\n|,|\./).filter(line => line.trim() !== '');
  };

  // Format phone numbers from string to array
  const formatPhones = (phoneString) => {
    if (!phoneString) return ['Contact not available'];
    const phones = phoneString.split(/,|\n/).filter(p => p.trim() !== '');
    return phones.map(p => p.trim());
  };

  // Get the full logo URL
  const getFullLogoUrl = (logoUrl) => {
    if (!logoUrl) return '/logo.png';
    if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
      return logoUrl;
    }
    if (logoUrl.startsWith('/admin') || logoUrl.startsWith('/')) {
      const baseUrl = websiteInfo?.website_url || 'https://devnarfoundationfortheblind.org';
      return `${baseUrl.replace(/\/$/, '')}${logoUrl}`;
    }
    return logoUrl;
  };

  // Social media icons mapping
  const socialIcons = {
    facebook: { icon: 'fa-facebook', color: '#1877f2', label: 'Facebook' },
    youtube: { icon: 'fa-youtube', color: '#ff0000', label: 'YouTube' },
    instagram: { icon: 'fa-instagram', color: '#e4405f', label: 'Instagram' },
    linkedin: { icon: 'fa-linkedin-in', color: '#0a66c2', label: 'LinkedIn' },
    twitter: { icon: 'fa-twitter', color: '#1da1f2', label: 'Twitter' }
  };

  // If loading, show a minimal footer
  if (loading) {
    return (
      <footer className="site-footer">
        <div className="wrap">
          <div className="footer-grid">
            <div className="footer-brand">
              <div className="footer-loading">
                <span className="loading-spinner"></span>
                <p>Loading...</p>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <div className="copy">© {new Date().getFullYear()} Devnar Foundation for the Blind — All Rights Reserved.</div>
          </div>
        </div>
      </footer>
    );
  }

  // Use fetched data or defaults
  const data = websiteInfo || defaults;
  const addressLines = parseAddress(data.address_en);
  const phoneNumbers = formatPhones(data.phone_number);
  const logoUrl = getFullLogoUrl(data.logo_url);
  const email = data.contact_email || defaults.contact_email;

  return (
    <footer className="site-footer">
      <div className="wrap">
        <div className="footer-grid">
          {/* Column 1: Logo & Description */}
          <div className="footer-brand">
            <img 
              src={logoUrl} 
              alt={data.app_name || 'Devnar Foundation for the Blind'} 
              className="footer-logo"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = '/logo.png';
              }}
            />
            <p className="brand-description">
              {data.app_name || 'Devnar Foundation for the Blind'} is a registered NGO 
              dedicated to the education and empowerment of persons with visual impairment. 
              12A &amp; 80G tax exemptions, FCRA accredited.
            </p>
            <div className="footer-badges">
              <span className="badge">12A</span>
              <span className="badge">80G</span>
              <span className="badge">FCRA</span>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div className="footer-links-column">
            <h4>Quick Links</h4>
            <ul className="footer-links">
              <li><Link to="/about">About Us</Link></li>
              <li><Link to="/programs">Programs</Link></li>
              <li><Link to="/gallery">Gallery</Link></li>
              <li><Link to="/csr">CSR</Link></li>
              <li><Link to="/volunteer">Volunteer</Link></li>
              <li><Link to="/contact">Contact</Link></li>
            </ul>
          </div>

          {/* Column 3: Contact Information */}
          <div className="footer-contact-column">
            <h4>Contact</h4>
            <div className="contact-info">
              <div className="contact-item">
                <i className="fas fa-map-marker-alt"></i>
                <span>
                  {addressLines.map((line, i) => (
                    <React.Fragment key={i}>
                      {line}{i < addressLines.length - 1 && ' '}
                    </React.Fragment>
                  ))}
                </span>
              </div>
              <div className="contact-item">
                <i className="fas fa-phone"></i>
                <span>
                  {phoneNumbers.map((phone, i) => (
                    <React.Fragment key={i}>
                      <a href={`tel:${phone.replace(/[^0-9+]/g, '')}`}>{phone}</a>
                      {i < phoneNumbers.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </span>
              </div>
              <div className="contact-item">
                <i className="fas fa-envelope"></i>
                <a className="mailto" href={`mailto:${email}`}>{email}</a>
              </div>
            </div>
          </div>

          {/* Column 4: Support & Social Media */}
          <div className="footer-support-column">
            <h4>Support Us</h4>
            <div className="support-buttons">
              <Link className="btn btn-primary" to="/donate">
                <i className="fas fa-hand-holding-heart"></i> Donate Now
              </Link>
            </div>
            
            {/* Social Media Links */}
            {(data.facebook || data.youtube || data.instagram || data.linkedin || data.twitter) && (
              <div className="social-section">
                <h5>Connect With Us</h5>
                <div className="social-links">
                  {data.facebook && (
                    <a 
                      href={data.facebook} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="social-icon facebook"
                      aria-label="Facebook"
                    >
                      <i className="fab fa-facebook-f"></i>
                    </a>
                  )}
                  {data.youtube && (
                    <a 
                      href={data.youtube} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="social-icon youtube"
                      aria-label="YouTube"
                    >
                      <i className="fab fa-youtube"></i>
                    </a>
                  )}
                  {data.instagram && (
                    <a 
                      href={data.instagram} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="social-icon instagram"
                      aria-label="Instagram"
                    >
                      <i className="fab fa-instagram"></i>
                    </a>
                  )}
                  {data.linkedin && (
                    <a 
                      href={data.linkedin} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="social-icon linkedin"
                      aria-label="LinkedIn"
                    >
                      <i className="fab fa-linkedin-in"></i>
                    </a>
                  )}
                  {data.twitter && (
                    <a 
                      href={data.twitter} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="social-icon twitter"
                      aria-label="Twitter"
                    >
                      <i className="fab fa-twitter"></i>
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Copyright Section */}
        <div className="footer-bottom">
          <div className="copy">
            © {new Date().getFullYear()} {data.app_name || 'Devnar Foundation for the Blind'} — All Rights Reserved.
            {data.website_url && data.website_url !== '#' && (
              <span className="website-link">
                <a href={data.website_url} target="_blank" rel="noopener noreferrer">
                  Visit Website
                </a>
              </span>
            )}
          </div>
          <div className="footer-credit">
            Built with <i className="fas fa-heart"></i> for the community
          </div>
        </div>
      </div>
    </footer>
  );
}